import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import Main from "./components/main";
import CookieConsent from "./components/CookieConsent";
import PageTransition from "./components/PageTransition";
import { AuthProvider } from "./contexts/AuthContext";
import AccessGate from "./components/access/AccessGate";
import SignInModal from "./components/access/SignInModal";
import { AppHome, LegacyConsoleRedirect } from "./components/app/appHomeRoutes";
import useGaPageViews from "./hooks/useGaPageViews";
import useScrollToHash from "./hooks/useScrollToHash";

const Gmap = lazy(() => import("./components/gmap"));
const AllContaminantsPage = lazy(() => import("./components/app/AllContaminantsPage"));
const FeaturesPage = lazy(() => import("./components/FeaturesPage"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const ArticlesPage = lazy(() => import("./components/ArticlesPage"));
const ArticlePage = lazy(() => import("./components/ArticlePage"));
const MyPropertiesPage = lazy(() => import("./components/MyPropertiesPage"));
const SettingsPage = lazy(() => import("./components/SettingsPage"));
const TermsPage = lazy(() => import("./components/legal/TermsPage"));
const PrivacyPage = lazy(() => import("./components/legal/PrivacyPage"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));
// Staff only: rendered as the gate's Outlet child behind StaffGuard, so a
// visitor who is not staff never downloads these chunks.
const StaffPage = lazy(() => import("./components/staff/StaffPage"));
const StaffTimelinePage = lazy(() => import("./components/staff/StaffTimelinePage"));
const StaffTeamPage = lazy(() => import("./components/staff/StaffTeamPage"));
const StaffAccessLogPage = lazy(() => import("./components/staff/StaffAccessLogPage"));

const LazyPage = ({ children }) => (
  <Suspense
    fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    }
  >
    {children}
  </Suspense>
);

/**
 * Each new page starts at the top. With `mode="wait"` the outgoing page has to
 * finish its exit before the next one mounts, and the browser never resets
 * the scroll on a client-side route change — so the new page used to arrive at
 * whatever depth the old one had been read to.
 *
 * Done on exit-complete rather than on location change, so the outgoing page
 * fades out where the reader left it instead of jumping to its own top first.
 * A `#hash` is not handled here: at exit-complete the new page hasn't mounted
 * yet, so its target can't exist. useScrollToHash waits for it and lands on it
 * after this reset.
 */
const scrollToNewPage = () => {
  window.scrollTo(0, 0);
};

const App = () => {
  const location = useLocation();
  // Scrubbed GA page views on route changes; nothing without analytics consent.
  useGaPageViews();
  // Anchored links (/features#how-it-works) land on their section.
  useScrollToHash();

  return (
    <AuthProvider>
      <ThemeProvider>
        {/* Skip link — first focusable element; visible only on keyboard focus
          (design review W1.7). Targets the content container below. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md focus:ring-2 focus:ring-ring"
        >
          Skip to main content
        </a>
        <div id="main-content" tabIndex={-1} className="outline-none">
          <AnimatePresence mode="wait" onExitComplete={scrollToNewPage}>
            <Routes location={location} key={location.pathname}>
              {/* Every page sits inside the one access gate, which reads its tier
                from the feature registry (config/features.json). A page added
                outside it fails access/registry.test.js. */}
              <Route element={<AccessGate />}>
                <Route
                  path="/"
                  element={
                    <PageTransition>
                      <Main />
                    </PageTransition>
                  }
                />
                {/* The console is the app: an address in the URL renders it, a bare
                /app is the entry screen. The map is a drill-down. */}
                <Route
                  path="/app"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <AppHome />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                {/* The console's rollout address. Kept as a redirect so shared and
                bookmarked links still resolve. */}
                <Route path="/app/v2" element={<LegacyConsoleRedirect />} />
                <Route
                  path="/app/contaminants"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <AllContaminantsPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/app/map"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <Gmap />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                {/* The How it works page folded into Features. The registry
                  classifies /process as a redirect to /features (its targets
                  are paths only); the #how-it-works fragment is added here and
                  delivered by useScrollToHash. */}
                <Route
                  path="/process"
                  element={<Navigate to="/features#how-it-works" replace />}
                />
                <Route
                  path="/features"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <FeaturesPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                {/* Restored: deleted with its route in 441c62a, 2026-07-25. */}
                <Route
                  path="/articles"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <ArticlesPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                {/* The article itself. Public, like the rest of the journal —
                  no auth gate. A slug with no published article falls through
                  to NotFoundPage inside the component, deliberately: a draft
                  and a nonexistent slug must be indistinguishable from
                  outside. */}
                <Route
                  path="/articles/:slug"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <ArticlePage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/about"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <AboutPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                {/* Both live under /app because both are app surfaces: they wear
                  the console's shell and none of the marketing chrome. The old
                  top-level paths redirect rather than 404 — they are in the
                  account menu of every build already shipped, and `replace`
                  keeps Back from bouncing off the redirect. */}
                <Route
                  path="/app/my-properties"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <MyPropertiesPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/my-properties"
                  element={<Navigate to="/app/my-properties" replace />}
                />
                <Route
                  path="/app/settings"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <SettingsPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/settings"
                  element={<Navigate to="/app/settings" replace />}
                />
                {/* The staff console (feature `staff-console`, guard `staff`):
                  the gate asks GET /api/v1/staff/me before any of these render. */}
                <Route
                  path="/app/staff"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <StaffPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/app/staff/team"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <StaffTeamPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/app/staff/access-log"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <StaffAccessLogPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/app/staff/users/:uid"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <StaffTimelinePage kind="user" />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/app/staff/sessions/:sessionId"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <StaffTimelinePage kind="session" />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <TermsPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
                <Route
                  path="/privacy"
                  element={
                    <LazyPage>
                      <PageTransition>
                        <PrivacyPage />
                      </PageTransition>
                    </LazyPage>
                  }
                />
              </Route>
              {/* Catch-all: unknown paths rendered a blank screen (Finding 9).
                Outside the gate: a 404 is open and fetches nothing, and an
                unknown path must never look like a sign-in prompt. */}
              <Route
                path="*"
                element={
                  <LazyPage>
                    <NotFoundPage />
                  </LazyPage>
                }
              />
            </Routes>
          </AnimatePresence>
        </div>
        <CookieConsent />
        {/* After the banner, and above it: the prompt must never be covered. */}
        <SignInModal />
        <Toaster position="top-center" />
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
